// One limit per rich-text field, in the characters a reader sees: what the editor counts and shows.
// The server keeps the same values and the same counting (application htmlSanitizeHelper.js).
export const RICH_TEXT_LIMITS = {
  appointmentTypeDescription: 3000,
  taskDescription: 2000,
  welcomeEmail: 3000,
  messageContent: 3000,
  messageSignature: 200,
  businessCardDescription: 400,
} as const;

// The markup may be this many times the visible limit: formatting never reaches it, a pasted page does.
export const HTML_SIZE_FACTOR = 10;

const segmenter =
  typeof Intl !== "undefined" && "Segmenter" in Intl
    ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
    : null;

// String.length counts an emoji as 2 to 8; the zero-width space keeps an empty inline code box open.
export const graphemes = (text: string): string[] => {
  const visible = text.replace(/​/g, "");
  return segmenter
    ? Array.from(segmenter.segment(visible), (part) => part.segment)
    : Array.from(visible);
};

export const countCharacters = (text: string) => graphemes(text).length;

export const textOf = (html: string): string =>
  html
    ? new DOMParser().parseFromString(html, "text/html").body.textContent ?? ""
    : "";

// Tags are dropped first, so the parser only ever decodes entities and never builds elements.
export const plainTextOf = (html?: string | null): string => {
  const text = (html ?? "").replace(/<[^>]*>/g, " ");
  if (!text.trim()) return "";
  return (
    new DOMParser().parseFromString(text, "text/html").body.textContent ?? ""
  )
    .replace(/\s+/g, " ")
    .trim();
};

export const richTextLength = (html: string | null | undefined): number =>
  countCharacters(textOf(html ?? "").trim());

export type RichTextProblem = "tooLong" | "tooHeavy" | null;

export const richTextProblem = (
  html: string | null | undefined,
  maxCharacters: number
): RichTextProblem => {
  if (richTextLength(html) > maxCharacters) return "tooLong";
  if ((html ?? "").length > maxCharacters * HTML_SIZE_FACTOR) return "tooHeavy";
  return null;
};

interface RuleHelpers {
  error: (code: string, context?: Record<string, unknown>) => unknown;
}

// For a Joi .custom(): the messages are "richText.tooShort", "richText.tooLong" ({{#count}}, {{#max}}) and "richText.tooHeavy".
export const richTextRule =
  (maxCharacters: number, minCharacters = 0) =>
  (value: string, helpers: RuleHelpers) => {
    const length = richTextLength(value);
    if (length < minCharacters)
      return helpers.error("richText.tooShort", { min: minCharacters });
    const problem = richTextProblem(value, maxCharacters);
    if (problem === "tooLong")
      return helpers.error("richText.tooLong", {
        count: length,
        max: maxCharacters,
      });
    if (problem === "tooHeavy") return helpers.error("richText.tooHeavy");
    return value;
  };
